/**
 * Export des données d'exportation (surfaces traitées) en Excel professionnel
 * Avec en-tête, titre, logo, tableau et pied de page
 */
import ExcelJS from "exceljs";

const APP_NAME = "Road Alert AGEROUTE";

export interface ExportationDataRowExcel {
  // Onglet 1 (surfaces-itineraire)
  itineraire?: string;
  dateitineraire?: string;
  surfacetraitee?: number;
  nbrepad?: number;
  nbrepcd?: number;
  communes?: string;
  libelledepartement?: string;
  // Onglet 2 (surfaces-departement)
  departement?: string;
  iddepartement?: number;
  annee?: string;
  // Onglet 3 (cumul-itineraire)
  idCumulItineraire?: number;
  mois?: string;
  surfacePrevisionnel?: number;
  surfaceTraite?: number;
  resteSurface?: number;
  statut?: number;
}

function formatDateFr(dateStr: string): string {
  if (!dateStr) return "-";
  try {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
}

/** Extraire la partie base64 d'une data URL pour ExcelJS */
function getBase64FromDataUrl(dataUrl: string): string | null {
  if (!dataUrl || !dataUrl.startsWith("data:")) return null;
  const base64 = dataUrl.split(",")[1];
  return base64 || null;
}

export interface ExportExportationDataExcelOptions {
  data: ExportationDataRowExcel[];
  dateDebut: string;
  dateFin: string;
  filename?: string;
  title?: string;
  /** Data URL du logo pour l'en-tête */
  logoDataUrl?: string | null;
}

const HEADER_ROW = 8;
const LOGO_WIDTH = 180;
const LOGO_HEIGHT = 48;

export async function exportExportationDataExcel({
  data,
  dateDebut,
  dateFin,
  filename = "rapport-exportation-data.xlsx",
  title = "Rapport des surfaces traitées par Itinéraire",
  logoDataUrl,
}: ExportExportationDataExcelOptions): Promise<void> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Rapport", { views: [{ showGridLines: true }] });

  // Détection automatique du type de données (onglet 1, 2 ou 3)
  const isDepartementTab = data.length > 0 && data[0]?.departement != null && data[0]?.iddepartement != null;
  const isCumulTab = data.length > 0 && data[0]?.surfacePrevisionnel != null;
  
  const HEADERS = isCumulTab
    ? ["Itinéraire", "Année", "Mois", "Surface prévisionnelle (m²)", "Surface traitée (m²)", "Reste surface (m²)"]
    : isDepartementTab
    ? ["Département", "Année", "Surface traitée (m²)", "PAD", "PCD"]
    : ["Itinéraire", "Date", "Surface traitée (m²)", "PAD", "PCD", "Commune"];
  
  ws.columns = isCumulTab
    ? [
        { width: 25 },
        { width: 12 },
        { width: 15 },
        { width: 22 },
        { width: 18 },
        { width: 18 },
      ]
    : isDepartementTab
    ? [
        { width: 25 },
        { width: 12 },
        { width: 18 },
        { width: 10 },
        { width: 10 },
      ]
    : [
        { width: 25 },
        { width: 15 },
        { width: 18 },
        { width: 10 },
        { width: 10 },
        { width: 20 },
      ];

  let rowIndex = 1;

  // --- Ligne 1 : logo + titre ---
  ws.getRow(rowIndex).height = logoDataUrl ? 36 : 22;
  if (logoDataUrl) {
    const base64 = getBase64FromDataUrl(logoDataUrl);
    if (base64) {
      const imageId = wb.addImage({
        base64,
        extension: "png",
      });
      ws.addImage(imageId, {
        tl: { col: 0.1, row: 0.15 },
        ext: { width: LOGO_WIDTH, height: LOGO_HEIGHT },
      });
    }
  }
  const titleCell = ws.getCell(rowIndex, 4);
  titleCell.value = APP_NAME;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { vertical: "middle" };
  rowIndex += 2;

  // --- Titre ---
  const titleRow = ws.getRow(rowIndex);
  titleRow.getCell(1).value = title;
  titleRow.getCell(1).font = { bold: true, size: 14 };
  rowIndex += 2;

  // --- Période ---
  ws.getRow(rowIndex).getCell(1).value = `Période : du ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`;
  rowIndex += 1;
  ws.getRow(rowIndex).getCell(1).value = `Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  ws.getRow(rowIndex).getCell(1).font = { color: { argb: "FF666666" } };
  rowIndex += 2;

  // --- En-têtes du tableau ---
  const headerRow = ws.getRow(HEADER_ROW);
  HEADERS.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF428BCA" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });
  headerRow.height = 22;

  // --- Données ---
  const moisNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];
  
  data.forEach((row, i) => {
    const r = ws.getRow(HEADER_ROW + 1 + i);
    const fill = i % 2 === 0 ? "FFF5F5F5" : "FFFFFFFF";
    let rowData: (string | number)[];
    
    if (isCumulTab) {
      const moisNum = row.mois ? parseInt(String(row.mois), 10) : null;
      const moisLabel = moisNum && moisNum >= 1 && moisNum <= 12 ? moisNames[moisNum - 1] : (row.mois ?? "-");
      rowData = [
        row.itineraire ?? "-",
        row.annee ?? "-",
        moisLabel,
        row.surfacePrevisionnel != null ? row.surfacePrevisionnel : "-",
        row.surfaceTraite != null ? row.surfaceTraite : "-",
        row.resteSurface != null ? row.resteSurface : "-",
      ];
    } else if (isDepartementTab) {
      rowData = [
        row.departement ?? "-",
        row.annee ?? "-",
        row.surfacetraitee != null ? row.surfacetraitee : "-",
        row.nbrepad != null ? row.nbrepad : "-",
        row.nbrepcd != null ? row.nbrepcd : "-",
      ];
    } else {
      rowData = [
        row.itineraire ?? "-",
        formatDateFr(row.dateitineraire ?? ""),
        row.surfacetraitee != null ? row.surfacetraitee : "-",
        row.nbrepad != null ? row.nbrepad : "-",
        row.nbrepcd != null ? row.nbrepcd : "-",
        row.communes ?? "-",
      ];
    }
    
    rowData.forEach((val, colIndex) => {
      const cell = r.getCell(colIndex + 1);
      cell.value = val;
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
      cell.alignment = { vertical: "middle" };
      cell.border = {
        top: { style: "thin", color: { argb: "FFCCCCCC" } },
        left: { style: "thin", color: { argb: "FFCCCCCC" } },
        bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
        right: { style: "thin", color: { argb: "FFCCCCCC" } },
      };
    });
  });

  // --- Pied de page ---
  const footerStart = HEADER_ROW + data.length + 3;
  ws.getRow(footerStart).getCell(1).value = `${APP_NAME} - ${title}`;
  ws.getRow(footerStart).getCell(1).font = { bold: true };
  ws.getRow(footerStart + 1).getCell(1).value = `Rapport généré le ${formatDateFr(new Date().toISOString().slice(0, 10))}`;
  ws.getRow(footerStart + 1).getCell(1).font = { color: { argb: "FF666666" } };

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
